"use client"

import React, {useEffect, useRef} from "react";

import Image from "next/image";

import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTAButton} from "@/shadcn/ui/button";
import {motion} from "framer-motion";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import {PageIQCapitalLaunchSchema} from "@/sanity/pages/PageIQCapitalLaunchTemplate";

import shine from "@/public/iq-capital-launch/box-shine.png";


const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeInDelay = (delay: number) => ({
  ...fadeIn,
  transition: { ...fadeIn.transition, delay }
});

export default function ProgressSection({
  data,
  className
}: {
  data: PageIQCapitalLaunchSchema;
  className?: string;
}) {
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!itemsRef.current.length) return;

    const animations = itemsRef.current.map((item) => {
      if (!item) return null;

      const shineElement = item.querySelector('.shine-bg') as HTMLElement;

      return gsap.fromTo(
        item,
        {
          opacity: 0.2,
        },
        {
          opacity: 1,
          scrollTrigger: {
            trigger: item,
            start: "top 60%",
            end: "top 20%",
            scrub: true,
            onUpdate: (self) => {
              if (shineElement) {
                const grayscaleValue = 100 - (self.progress * 100);
                shineElement.style.filter = `grayscale(${grayscaleValue}%)`;
              }
            },
          },
        }
      );
    });

    return () => {
      animations.forEach((anim) => {
        if (anim) {
          anim.scrollTrigger?.kill();
          anim.kill();
        }
      });
    };
  }, [data?.progressList]);

  return (
    <section className={cn(
      "relative pb-[14rem] lg:pb-[20rem]",
      className
    )}>
      <div className="container flex flex-col items-center">
        <div className="w-[min(148.5rem,100%)] flex flex-col lg:flex-row items-center lg:items-start lg:justify-between gap-[3rem]">
          <div className="w-[min(66rem,100%)] flex flex-col items-center lg:items-start">
            <motion.div
              {...fadeInDelay}
              className="bg-[#1A1A1A]/70 rounded-full border border-white/20 mb-[2rem]"
            >
              <p className="mb-0 leading-none text-white px-[3rem] py-[1.75rem] font-bold uppercase">{data?.progressChip}</p>
            </motion.div>

            {data?.progressTitle && (
              <motion.div
                {...fadeInDelay(0.1)}
                className="richtext lg:richtext-start prose-h2:mb-[1.5rem]"
              >
                <PortableText
                  value={data.progressTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            {data?.progressCTA && (
              <motion.div {...fadeInDelay(0.2)}>
                <CTAButton
                  className="mt-[5rem] hidden lg:block"
                  data={data.progressCTA}
                />
              </motion.div>
            )}
          </div>

          <div className="w-[min(66rem,100%)] lg:w-[min(60rem,100%)] h-full flex gap-[2rem] relative">
            <div className="absolute left-0 top-0 h-full w-[1.25rem] overflow-clip">
              <div className="relative w-full h-[calc(100%-1rem)] pt-[1rem]">
                <div className="absolute left-1/2 -translate-x-1/2 top-0 w-[2px] h-full bg-gradient-to-b from-[#61FFCE]/10 to-[#61FFCE]/20  rounded-full opacity-70"/>
                <div className="sticky top-[50%] after:h-[100vh] after:w-[2px] after:bg-gradient-to-b after:from-[#61FFCE]/0 after:from-50% after:to-[#61FFCE] after:rounded-full after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-0"/>
                <div className="sticky w-[1rem] h-[1rem] bg-[#61FFCE] top-[50%] translate-x-[.125rem] rotate-[45deg]"/>
              </div>
            </div>

            <div className="pl-[4rem] flex flex-col gap-[2rem]">
              <p className="mb-0 text-[2.25rem] text-white/60">Enthalten:</p>

              {data?.progressList && data.progressList.map((item, index) => (
                <div
                  key={index}
                  ref={(el) => {
                    itemsRef.current[index] = el;
                  }}
                  className="bg-white/5 gradient-border-white-2 before:opacity-10 relative rounded-[2.25rem] p-[.75rem] overflow-clip"
                  style={{ opacity: 0.2 }}
                >
                  <div className="p-[2rem] sm:py-[3rem] sm:pl-[3rem] lg:py-[4rem] flex items-start gap-[3rem] bg-black/5 gradient-border-white-2 before:opacity-10 relative rounded-[1.75rem] overflow-clip">
                    <Image
                      className="shine-bg absolute top-0 -left-[6rem] max-w-[unset] w-[calc(100%+12rem)] object-cover object-top"
                      src={shine}
                      alt=""
                      style={{ filter: 'grayscale(100%)' }}
                    />

                    {item?.icon && (
                      <Image
                        className="!w-[7rem] flex-shrink-0 relative"
                        src={item?.icon}
                        alt=""
                      />
                    )}

                    {item?.text && (
                      <div className="richtext richtext-start prose-h3:text-[3rem] prose-h3:mb-[.5rem] relative">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {data?.progressCTA && (
          <CTAButton
            className="mt-[4rem] lg:hidden"
            data={data.progressCTA}
          />
        )}
      </div>
    </section>
  );
}
